import type { EmployerVerificationDetail } from '../../api/employerVerificationDashboardApi';
import {
  buildRoleMismatchExplanation,
  readRoleMismatchContext,
} from './roleMismatchFindingPresentation';
import { buildCommuteFindingCaseSummary } from './commuteFindingPresentation';
import {
  buildDigitalPresenceFindingContent,
  LIMITED_DIGITAL_PRESENCE_FINDING_ID,
} from './digitalPresenceFindingPresentation';
import { COMMUTE_DISTANCE_FINDING_ID } from './findingVisualTheme';
import { parseCreditAdjudicatorMessage } from './parseCreditAdjudicatorMessage';
import type { ReviewReason, ReviewReasonSeverity } from './reviewReason.types';

export type AdjudicatorFindingBrief = {
  findingId: string;
  title: string;
  severity: ReviewReasonSeverity;
  source: 'ai_role_sector' | 'ai_linkage' | 'template';
  advisoryLabel: string | null;
  caseSummary: string;
  caseSummaryPoints?: string[];
  reviewMeasures: string;
  reviewMeasuresPoints?: string[];
  followUpEvidence: string;
  followUpPoints?: string[];
  footerNote: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function formatAdvisoryLabel(input: {
  suggestedOutcome: unknown;
  confidence: unknown;
}): string | null {
  const suggested = readString(input.suggestedOutcome)?.toUpperCase();
  if (suggested !== 'PASS' && suggested !== 'REVIEW') return null;
  let label = `Advisory ${suggested}`;
  if (typeof input.confidence === 'number' && Number.isFinite(input.confidence)) {
    const pct =
      input.confidence <= 1
        ? Math.round(input.confidence * 100)
        : Math.round(input.confidence);
    label = `${label} · ${pct}% confidence`;
  }
  return label;
}

function briefFromMessage(input: {
  finding: ReviewReason;
  source: AdjudicatorFindingBrief['source'];
  message: string;
  advisoryLabel: string | null;
  footerNote: string | null;
  detail?: EmployerVerificationDetail | null;
  footprint?: { companyProfile?: { sector?: string | null } | null } | null;
}): AdjudicatorFindingBrief {
  const sections = parseCreditAdjudicatorMessage(input.message);
  const baseSummary = sections.caseSummary || input.finding.explanation;
  return {
    findingId: input.finding.id,
    title: input.finding.title,
    severity: input.finding.severity,
    source: input.source,
    advisoryLabel: input.advisoryLabel,
    caseSummary: baseSummary,
    reviewMeasures:
      sections.reviewMeasures || input.finding.recommendedAction || '',
    followUpEvidence: sections.followUpEvidence,
    footerNote: input.footerNote,
  };
}

function templateBrief(finding: ReviewReason): AdjudicatorFindingBrief {
  return {
    findingId: finding.id,
    title: finding.title,
    severity: finding.severity,
    source: 'template',
    advisoryLabel: null,
    caseSummary: finding.explanation,
    reviewMeasures: finding.recommendedAction ?? '',
    followUpEvidence: '',
    footerNote: null,
  };
}

/** Map a reviewer finding to an adjudicator action brief (AI-first when available). */
export function resolveAdjudicatorFindingBrief(
  finding: ReviewReason,
  detail: EmployerVerificationDetail | null | undefined,
  footprint?: { companyProfile?: { sector?: string | null } | null } | null,
): AdjudicatorFindingBrief {
  const evidence = asRecord(detail?.evidence);

  if (finding.id === 'role-mismatch') {
    const ai = asRecord(evidence?.aiRoleSectorAnalysis);
    const omitted = ai?.omitted === true;
    const message = readString(ai?.creditAdjudicatorMessage);
    if (message && !omitted) {
      return briefFromMessage({
        finding,
        source: 'ai_role_sector',
        message,
        advisoryLabel: formatAdvisoryLabel({
          suggestedOutcome: ai?.suggestedOutcome,
          confidence: ai?.confidence,
        }),
        footerNote:
          'Automated role & sector pre-read — does not change the verification outcome.',
        detail,
        footprint,
      });
    }

    const ctx = readRoleMismatchContext({ detail, footprint: footprint as never });
    return {
      findingId: finding.id,
      title: finding.title,
      severity: finding.severity,
      source: 'template',
      advisoryLabel: null,
      caseSummary: buildRoleMismatchExplanation(ctx),
      reviewMeasures: finding.recommendedAction ?? 'Confirm the role and industry fit with the declared employer.',
      followUpEvidence:
        'Request employment confirmation: offer letter or contract, recent pay stub showing employer name, and HR/payroll letter if the workplace type is unclear.',
      followUpPoints: [
        'Offer letter or contract showing employer name',
        'Recent pay stub showing employer name',
        'HR or payroll letter if the workplace type is unclear',
      ],
      footerNote: null,
    };
  }

  if (finding.id === 'income-source-mismatch' || finding.id === 'payroll-name-mismatch') {
    const ai = asRecord(evidence?.aiLinkageAnalysis);
    const message = readString(ai?.creditAdjudicatorMessage);
    if (message) {
      return briefFromMessage({
        finding,
        source: 'ai_linkage',
        message,
        advisoryLabel: formatAdvisoryLabel({
          suggestedOutcome: ai?.suggestedOutcome,
          confidence: ai?.confidence,
        }),
        footerNote:
          'Automated payroll linkage pre-read — does not change the verification outcome.',
        detail,
        footprint,
      });
    }
  }

  if (finding.id === COMMUTE_DISTANCE_FINDING_ID) {
    return {
      findingId: finding.id,
      title: finding.title,
      severity: finding.severity,
      source: 'template',
      advisoryLabel: null,
      caseSummary: buildCommuteFindingCaseSummary(
        finding,
        detail ?? null,
        footprint as never,
      ),
      reviewMeasures: finding.recommendedAction ?? '',
      followUpEvidence:
        'Confirm work arrangement (onsite vs remote) and request proof of workplace if commute is implausible.',
      followUpPoints: [
        'Confirm work arrangement (onsite vs remote)',
        'Request proof of workplace if commute is implausible',
      ],
      footerNote: null,
    };
  }

  if (finding.id === LIMITED_DIGITAL_PRESENCE_FINDING_ID) {
    const content = buildDigitalPresenceFindingContent(finding, detail ?? null);
    return {
      findingId: finding.id,
      title: finding.title,
      severity: finding.severity,
      source: 'template',
      advisoryLabel: null,
      caseSummary: content.caseSummary,
      caseSummaryPoints: content.caseSummaryPoints,
      reviewMeasures: content.reviewMeasures,
      reviewMeasuresPoints: content.reviewMeasuresPoints,
      followUpEvidence:
        'Request offer letter, recent pay stub, or HR contact if public web signals stay inconclusive.',
      followUpPoints: [
        'Offer letter or contract',
        'Recent pay stub',
        'HR contact if public web signals stay inconclusive',
      ],
      footerNote: null,
    };
  }

  return templateBrief(finding);
}

export function resolveAdjudicatorFindingBriefs(
  findings: ReviewReason[],
  detail: EmployerVerificationDetail | null | undefined,
  footprint?: { companyProfile?: { sector?: string | null } | null } | null,
): AdjudicatorFindingBrief[] {
  return findings.map((finding) =>
    resolveAdjudicatorFindingBrief(finding, detail, footprint),
  );
}
